
import React, { useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { AuthContext } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { CalendarBackgroundServices } from '@/lib/calendar/background-services';
import { clearOnboardingStorage } from '@/lib/utils/onboarding-storage';
import { trackRefgrowEvent } from '@/lib/refgrow';
import { updateSentryUserContext } from '@/lib/sentry-utils';
import { syncUserPlanToMixpanel } from '@/lib/mixpanel-user-sync';
import { safeMixpanelIdentify, safeMixpanelPeopleSet, safeMixpanelTrack, retryMixpanelOperation } from '@/lib/mixpanel-utils';


// Crisp global type declaration
declare global {
  interface Window {
    $crisp?: any[];
    CRISP_TOKEN_ID?: string | null;
    CRISP_RUNTIME_CONFIG?: {
      session_merge?: boolean;
    };
  }
}


export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Track users we're already processing to prevent duplicates
  const processingNewUsers = useRef<Set<string>>(new Set());




  // Only run health check for users who have calendar connections
  useEffect(() => {
    if (user?.id) {
      // Start calendar background services for authenticated users
      setTimeout(async () => {
        try {
          console.log('🚀 Starting calendar background services for user:', user.id);
          const backgroundServices = CalendarBackgroundServices.getInstance();
          backgroundServices.start();
          
          // Also check if user has calendar connections and force initial healing
          const { data: connections } = await supabase
            .from('calendar_connections')
            .select('id')
            .eq('user_id', user.id)
            .limit(1);
          
          if (connections && connections.length > 0) {
            console.log('📊 User has calendar connections, performing initial healing check');
            await backgroundServices.forceHeal();
          }
        } catch (error) {
          console.error('[AuthProvider] Error starting calendar background services:', error);
        }
      }, 1000); // Delay to let auth settle
    }
  }, [user?.id]);

  useEffect(() => {
    // Set up auth state listener FIRST (to catch any events during initialization)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Only log significant auth events, not every state change
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          console.log("[AuthProvider] Auth event:", event);
        }
        
        // Only synchronous state updates here
        setSession(newSession);
        setUser(newSession?.user || null);
        
        // Update Sentry user context for feedback auto-population
        updateSentryUserContext(newSession?.user || null);
        
        // Identify user in Mixpanel when they sign in
        if (event === 'SIGNED_IN' && newSession?.user) {
          const user = newSession.user;
          
          // Use retry mechanism for identify since it's critical for user tracking
          retryMixpanelOperation(() => {
            safeMixpanelIdentify(user.id);
            safeMixpanelPeopleSet({
              '$name': user.user_metadata?.name || user.email?.split('@')[0] || 'User',
              '$email': user.email || '',
              'created_at': user.created_at,
              // Add any additional user properties here
            });
            return true;
          });

          // Sync user plan information to Mixpanel (this function has its own safety checks)
          syncUserPlanToMixpanel(user.id);

          // Track Sign Up or Sign In event based on whether this is a new user
          // Check if user was just created (created_at is very recent, within last minute)
          const userCreatedAt = new Date(user.created_at);
          const now = new Date();
          const timeSinceCreation = now.getTime() - userCreatedAt.getTime();
          const isNewUser = timeSinceCreation < 60000; // Created within last minute

          // Get UTM parameters from URL
          const urlParams = new URLSearchParams(window.location.search);
          const utmSource = urlParams.get('utm_source');
          const utmMedium = urlParams.get('utm_medium');
          const utmCampaign = urlParams.get('utm_campaign');

          // Detect signup method from user metadata or app metadata
          const signupMethod = user.app_metadata?.provider || user.user_metadata?.provider || 'email';
          const loginMethod = user.app_metadata?.provider || user.user_metadata?.provider || 'email';

          if (isNewUser) {
            // Track Sign Up event
            safeMixpanelTrack('Sign Up', {
              user_id: user.id,
              email: user.email || '',
              signup_method: signupMethod,
              utm_source: utmSource || null,
              utm_medium: utmMedium || null,
              utm_campaign: utmCampaign || null,
            });
          } else {
            // Track Sign In event
            safeMixpanelTrack('Sign In', {
              user_id: user.id,
              login_method: loginMethod,
              success: true,
            });
          }
        }
        
        // Clear onboarding localStorage whenever auth state changes to prevent data leakage
        // Only process SIGNED_IN events to avoid duplicate processing
        if (event === 'SIGNED_IN' && newSession?.user) {
          // Use a timeout to batch onboarding storage operations and prevent duplicates
          setTimeout(() => {
            // Check if this is a new user by looking at user metadata
            const isNewUser = !newSession.user.user_metadata?.onboarding_current_step;
            
            if (isNewUser) {
              // Always clear for new users to start fresh onboarding
              clearOnboardingStorage();
              console.log("[AuthProvider] Cleared onboarding localStorage for new user");
            } else {
              // For existing users, preserve if they're in the middle of onboarding
              const currentOnboardingStep = localStorage.getItem('onboarding-current-step');
              const isInOnboarding = currentOnboardingStep !== null && currentOnboardingStep !== '0';
              
              if (!isInOnboarding) {
                clearOnboardingStorage();
                console.log("[AuthProvider] Cleared onboarding localStorage on auth state change");
              } else {
                console.log("[AuthProvider] Preserving onboarding localStorage - user is in onboarding flow");
              }
            }
          }, 100); // Small delay to batch operations
        }

        // Manage Crisp session tokens and verification when they sign in
        if (event === 'SIGNED_IN' && newSession?.user) {
          setTimeout(async () => {
            if (typeof window !== 'undefined' && window.$crisp) {
              try {
                // Get user's crisp token for session association
                const { data: tokenData, error: tokenError } = await supabase.functions.invoke('crisp-get-token');

                if (tokenError) {
                  console.error("[AuthProvider] Failed to get Crisp token:", tokenError);
                } else {
                  // Set the token for session association
                  window.CRISP_TOKEN_ID = tokenData.token;
                  console.log("[AuthProvider] Crisp token set for user:", newSession.user.id);
                }

                // Get signed email for verification
                const { data: signData, error: signError } = await supabase.functions.invoke('crisp-sign-email', {
                  body: { email: newSession.user.email }
                });

                if (signError) {
                  console.error("[AuthProvider] Failed to sign email for Crisp:", signError);
                  // Fallback to unsigned email
                  window.$crisp.push(["set", "user:email", newSession.user.email]);
                } else {
                  // Use signed email with verification
                  window.$crisp.push(["set", "user:email", [signData.email, signData.signature]]);
                  console.log("[AuthProvider] User verified and identified to Crisp:", signData.email);
                }

                // Reset session to apply new token and user data
                window.$crisp.push(["do", "session:reset"]);
                
              } catch (error) {
                console.error("[AuthProvider] Failed to identify user to Crisp:", error);
                // Fallback to unsigned email
                try {
                  window.$crisp.push(["set", "user:email", newSession.user.email]);
                } catch (fallbackError) {
                  console.error("[AuthProvider] Fallback also failed:", fallbackError);
                }
              }
            }
          }, 1000); // Small delay to ensure Crisp is loaded
        }

        // Clear Crisp session when user signs out
        if (event === 'SIGNED_OUT') {
          // Set logout state to prevent sidebar errors during transition
          setIsLoggingOut(true);
          
          setTimeout(() => {
            if (typeof window !== 'undefined' && window.$crisp) {
              try {
                // Clear the token and user email
                window.CRISP_TOKEN_ID = null;
                window.$crisp.push(["set", "user:email", null]);
                
                // Reset session to unbind from the previous user's conversation
                window.$crisp.push(["do", "session:reset"]);
                
                console.log("[AuthProvider] User cleared from Crisp and session reset");
              } catch (error) {
                console.error("[AuthProvider] Failed to clear user from Crisp:", error);
              }
            }
            
            // Clear onboarding localStorage when user signs out to prevent data leakage
            clearOnboardingStorage();
            console.log("[AuthProvider] Cleared onboarding localStorage on sign out");
            
            // Reset logout state after cleanup
            setIsLoggingOut(false);
          }, 100);
        }

        // Clear onboarding localStorage immediately when user signs in to prevent data leakage
        // For new users, always clear to start fresh
        if (event === 'SIGNED_IN' && newSession?.user) {
          // Check if this is an OAuth return (wearable or calendar) by looking at current URL
          const currentUrl = window.location.href;
          const isOAuthReturn = currentUrl.includes('wearable_connected=') || currentUrl.includes('calendar_connected=');
          
          if (isOAuthReturn) {
            console.log("[AuthProvider] OAuth return detected, preserving onboarding localStorage");
            // Don't clear storage during OAuth returns - let OnboardingPage handle it
            return;
          }
          
          // Check if this is a new user by looking at user metadata
          const isNewUser = !newSession.user.user_metadata?.onboarding_current_step;
          
          if (isNewUser) {
            // Always clear for new users to start fresh onboarding
            clearOnboardingStorage();
            console.log("[AuthProvider] Cleared onboarding localStorage for new user");
          } else {
            // For existing users, preserve if they're in the middle of onboarding
            const currentOnboardingStep = localStorage.getItem('onboarding-current-step');
            const isInOnboarding = currentOnboardingStep !== null && currentOnboardingStep !== '0';
            
            if (!isInOnboarding) {
              clearOnboardingStorage();
              console.log("[AuthProvider] Cleared onboarding localStorage on sign in");
            } else {
              console.log("[AuthProvider] Preserving onboarding localStorage on sign in - user is in onboarding flow");
            }
          }
          
          // Prevent duplicate processing of the same user (atomically check and add)
          // Check BEFORE setTimeout to prevent race conditions from multiple auth events
          const userId = newSession.user.id;
          if (processingNewUsers.current.has(userId)) {
            console.log("[AuthProvider] Already processing new user setup for", userId);
            return;
          }
          
          // Add to processing set immediately to prevent race conditions
          processingNewUsers.current.add(userId);
          
          // Defer trial check to prevent authentication hanging
          setTimeout(async () => {
            
            try {
              
              try {
                // Check if this is a new user by checking if they have any subscription data
                const { data: existingSubscriber } = await supabase
                  .rpc('get_user_subscription_status', { p_user_id: newSession.user.id })
                  .maybeSingle();

                // If user has no subscription data, check if they have existing Stripe subscriptions
                if (!existingSubscriber || existingSubscriber.subscription_status === 'none') {
                  // Try to consolidate existing Stripe customer data
                  try {
                    const consolidateResponse = await fetch('/api/consolidate-customer', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${newSession.access_token}`,
                      },
                      body: JSON.stringify({
                        email: newSession.user.email,
                        userId: newSession.user.id,
                      }),
                    });

                    if (consolidateResponse.ok) {
                      const consolidateData = await consolidateResponse.json();
                      if (consolidateData.consolidated) {
                        console.log('[AuthProvider] Consolidated existing Stripe subscriptions:', consolidateData);
                        // Re-check subscription status after consolidation
                        const { data: updatedSubscriber } = await supabase
                          .rpc('get_user_subscription_status', { p_user_id: newSession.user.id })
                          .maybeSingle();
                        
                        if (updatedSubscriber?.subscription_status !== 'none') {
                          console.log('[AuthProvider] User now has subscription after consolidation:', updatedSubscriber);
                          return; // Skip trial creation since user has existing subscription
                        }
                      }
                    }
                  } catch (error) {
                    console.error('[AuthProvider] Failed to consolidate customer:', error);
                  }
                  // This is a new user, start their trial
                  console.log("[AuthProvider] Starting trial for new user", {
                    userId: newSession.user.id,
                    email: newSession.user.email,
                    timestamp: new Date().toISOString()
                  });

                  try {
                    await supabase.functions.invoke('start-trial', {
                      body: { email: newSession.user.email }
                    });
                    console.log("[AuthProvider] Trial started successfully");
                    
                    // Send welcome email only to TRULY new users (check if they've ever received one)
                    try {
                      // Check if welcome email was already sent to prevent duplicates
                      const { data: existingWelcomeEmail } = await supabase
                        .from('sent_emails')
                        .select('id')
                        .eq('user_id', newSession.user.id)
                        .eq('template_name', 'welcome')
                        .maybeSingle();
                      
                      if (!existingWelcomeEmail) {
                        const firstName = newSession.user.user_metadata?.full_name?.split(' ')[0] || 
                                          newSession.user.email?.split('@')[0] || 'there';
                        
                        await supabase.functions.invoke('send-email', {
                          body: { 
                            userId: newSession.user.id,
                            email: newSession.user.email,
                            firstName,
                            template: 'welcome'
                          }
                        });
                        console.log("[AuthProvider] Welcome email sent successfully");
                      } else {
                        console.log("[AuthProvider] Skipping welcome email - already sent to this user");
                      }
                    } catch (emailError) {
                      console.error("[AuthProvider] Failed to send welcome email:", emailError);
                    }

                  // Track successful signup with Refgrow for new users
                  trackRefgrowEvent(0, 'signup', newSession.user.email);

                  // Track user signup in our analytics
                  await trackEvent({
                    name: 'user_signup',
                    properties: {
                      signupMethod: 'oauth',
                      userId: newSession.user.id,
                    }
                  });
                } catch (error) {
                  console.error("[AuthProvider] Failed to start trial:", error);
                }
              } else {
                // Existing user logging in - check if they should go to onboarding or main app
                console.log("[AuthProvider] Existing user logged in", {
                  userId: newSession.user.id,
                  email: newSession.user.email,
                  timestamp: new Date().toISOString()
                });
                
                // Check if user has completed onboarding
                const { data: userSettings } = await supabase
                  .from('user_settings')
                  .select('completed_onboarding')
                  .eq('user_id', newSession.user.id)
                  .maybeSingle();
                
                // Check if this is a wearable connection return
                const currentUrl = window.location.href;
                const isWearableConnectionReturn = currentUrl.includes('wearable_connected=') || currentUrl.includes('biostrap_connected=');
                
                if (userSettings?.completed_onboarding === false && !isWearableConnectionReturn) {
                  // User hasn't completed onboarding and this is NOT a wearable connection return
                  // Only redirect to onboarding if they're not in the middle of connecting a wearable
                  console.log("[AuthProvider] Existing user hasn't completed onboarding, redirecting to onboarding");
                  // The redirect will be handled by AuthRedirect component
                } else if (userSettings?.completed_onboarding === false && isWearableConnectionReturn) {
                  // User hasn't completed onboarding but this IS a wearable connection return
                  // Don't redirect - let them continue from their current onboarding step
                  console.log("[AuthProvider] Existing user returning from wearable connection, preserving onboarding progress");
                } else {
                  // User has completed onboarding, but don't redirect immediately during OAuth flows
                  // Let the OAuth callback handler decide what to do
                  console.log("[AuthProvider] Existing user has completed onboarding");
                  
                  // Only redirect if this is NOT an OAuth flow (i.e., user is already on onboarding page)
                  // During OAuth flows, the callback handler will show the dialog
                  const isOAuthFlow = window.location.search.includes('access_token') || 
                                    window.location.search.includes('code') ||
                                    window.location.search.includes('error');
                  
                  // Check if this is specifically an OAuth error (not just any error)
                  const isOAuthError = window.location.search.includes('error=') && 
                                     (window.location.search.includes('error_description=') ||
                                      window.location.search.includes('error_code='));
                  
                  if (!isOAuthFlow && window.location.pathname === '/onboarding') {
                    // Use a small delay to prevent rapid redirects
                    setTimeout(() => {
                      window.location.href = '/meeting';
                    }, 100);
                  } else if (isOAuthError && window.location.pathname === '/onboarding') {
                    console.log("[AuthProvider] OAuth error detected during onboarding - letting onboarding page handle it gracefully");
                    // Don't redirect - let the onboarding page handle the OAuth error gracefully
                  }
                }

                await trackEvent({
                  name: 'user_login',
                  properties: {
                    signupMethod: 'oauth',
                    userId: newSession.user.id,
                  }
                });
              }
              } finally {
                // Always clean up processing flag
                processingNewUsers.current.delete(userId);
              }
            } catch (error) {
              console.error("[AuthProvider] Error checking subscriber:", error);
              // Clean up processing flag on error too
              processingNewUsers.current.delete(userId);
            }
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user || null);
      setLoading(false);
      setInitialized(true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, initialized, isLoggingOut }}>
      {children}
    </AuthContext.Provider>
  );
}
